import React, { useCallback, useMemo, useState } from 'react';
import { Autocomplete, Chip, Box, IconButton } from '@mui/material';
import { ValidationRule } from '@core/utils/validation.ts';
import { useFormField } from '@core/hooks/useFormField';
import { WcnTextField } from '@/components';

interface FormComboBoxProps {
    name: string;
    value: string[];
    items?: any[];
    itemText?: string;
    itemValue?: string;
    label?: string;
    rules?: ValidationRule[];
    onChange?: (value: string[]) => void;
    freeSolo?: boolean;
    hidden?: boolean;
    fullWidth?: boolean;
    renderChip?: boolean;
    variant?: 'outlined' | 'standard' | 'filled';
    chipPlacement?: 'inside' | 'outside';
    required?: boolean;
}

const FormComboBox: React.FC<FormComboBoxProps> = ({
    name,
    value,
    items = [],
    itemText,
    label,
    rules = [],
    onChange,
    variant = 'standard',
    freeSolo = true,
    hidden = false,
    fullWidth = false,
    renderChip = true,
    chipPlacement = 'inside',
    required
}) => {
    const { error, showError, handleBlur, handleChange } = useFormField({
        name,
        label,
        value,
        rules
    });

    const [inputValue, setInputValue] = useState('');

    // Memoize options to prevent recalculation on every render
    const options = useMemo(() => {
        return (items || []).map((it: any) => {
            if (!it && it !== 0) return '';
            if (itemText) return String(it[itemText]);
            return String(it);
        });
    }, [items, itemText]);

    const handleAutocompleteChange = (_event: any, newValue: any) => {
        const normalized = Array.isArray(newValue)
            ? newValue.map((v: any) => (typeof v === 'string' ? v : String(v)))
            : [];
        onChange?.(normalized);
        handleChange(normalized);
    };

    if (hidden) {
        const hiddenValue = Array.isArray(value) ? value.join(',') : (value ?? '');
        return <input type="hidden" name={name} value={hiddenValue} aria-hidden="true" />;
    }

    const handleDeleteChip = (option: string) => {
        const next = (value || []).filter((v) => v !== option);
        onChange?.(next);
        handleChange(next);
    };

    const clearInput = useCallback(() => {
        setInputValue('');
    }, []);

    // Memoize render tags function to prevent recreation
    const renderTags = useMemo(() => {
        if (!renderChip || chipPlacement === 'outside') return () => null;

        return (tagValue: readonly string[], getTagProps: any) => (
            <Box className="wcn-text-field-chips">
                {tagValue.map((option, index) => (
                    <Chip
                        key={option}
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        className="email-chip"
                    />
                ))}
            </Box>
        );
    }, [renderChip, chipPlacement]);

    return (
        <Box
            sx={
                chipPlacement === 'outside'
                    ? { display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }
                    : undefined
            }
        >
            <Autocomplete
                multiple
                disableClearable
                freeSolo={freeSolo}
                options={options}
                value={value}
                inputValue={inputValue}
                onInputChange={(_, value) => setInputValue(value)}
                onChange={handleAutocompleteChange}
                renderTags={renderTags}
                sx={chipPlacement === 'outside' ? { flex: '0 0 50%', maxWidth: '50%' } : undefined}
                renderInput={(params) => (
                    <WcnTextField
                        {...params}
                        label={label}
                        onBlur={handleBlur}
                        error={showError}
                        helperText={showError ? error : undefined}
                        fullWidth={fullWidth}
                        variant={variant}
                        required={required}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: inputValue ? (
                                <IconButton
                                    size="small"
                                    sx={{
                                        padding: '2px',
                                        color: 'var(--gray-medium)',
                                        '& .bi': { fontSize: '16px', lineHeight: 1 }
                                    }}
                                    onMouseDown={(e) => {
                                        if (e.button !== 0) return;
                                        e.preventDefault();
                                        clearInput();
                                    }}
                                >
                                    <i className="bi bi-x-lg" />
                                </IconButton>
                            ) : (
                                params.InputProps.endAdornment
                            )
                        }}
                    />
                )}
            />
            {chipPlacement === 'outside' && (value || []).length > 0 && (
                <Box className="combo-chip-grid">
                    {(value || []).map((option) => (
                        <Chip
                            key={option}
                            label={option}
                            size="small"
                            className="email-chip combo-chip--grid"
                            onDelete={() => handleDeleteChip(option)}
                            deleteIcon={<i className="bi bi-x-circle-fill" />}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

FormComboBox.displayName = 'FormComboBox';

export default FormComboBox;
